import {
  Box,
  Button,
  ButtonList,
  DropdownButton,
  Table,
} from '@dixa/lib-shared-habla';

import { Secret } from 'apis/messengers';

import styles from './automaticUserVerificationSetup.module.scss';
import { i18nShortDate } from './i18nShortDate';

export type AutomaticUserVerificationSetupProps = {
  /** Add classes to the root element. */
  className?: string;
  /** List of secrets. */
  secrets: Secret[];
  /** Set to true if the list is loading. */
  isLoading?: boolean;
  /** Triggers when add new key button is clicked. */
  onAdd: () => void;
  /**
   * Triggers when row or edit key option is clicked.
   *
   * @param id - Id of the secret.
   */
  onEdit: (id: string) => void;
  /**
   * Triggers when delete key option is clicked.
   *
   * @param id - Id of the secret.
   */
  onDelete: (id: string) => void;
};

/** List messenger secrets with actions for managing them. */
export function AutomaticUserVerificationSetup({
  className,
  secrets,
  isLoading,
  onAdd,
  onEdit,
  onDelete,
}: AutomaticUserVerificationSetupProps): JSX.Element {
  const handleOnEditClick = (id: string) => () => {
    onEdit(id);
  };

  const button = (
    <Box margin="300 0 0">
      <Button
        variant="secondary"
        type="button"
        disabled={isLoading}
        onClick={onAdd}
        icon="plus"
      >
        Add new key
      </Button>
    </Box>
  );

  return (
    <div className={className}>
      <Table
        hoverable
        loading={isLoading}
        emptyState={
          isLoading !== true && secrets.length === 0
            ? {
                title: 'Add a key to set up user verification.',
                description: button,
              }
            : undefined
        }
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th className={styles.tableCreatedAtHeader}>
              Creation date
            </Table.Th>
            <Table.Th>Key description</Table.Th>
            <Table.Th></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {secrets.map(({ id, createdAt, description }) => (
            <Table.Tr key={id} className={styles.tableRow}>
              <Table.Td onClick={handleOnEditClick(id)}>
                {i18nShortDate(createdAt)}
              </Table.Td>
              <Table.Td onClick={handleOnEditClick(id)}>{description}</Table.Td>
              <Table.Td align="right">
                <DropdownButton
                  icon="dots-h"
                  fixed
                  buttonProps={{ variant: 'tertiary' }}
                  noArrow
                  dropdown={
                    <ButtonList>
                      <ButtonList.Item
                        buttonProps={{ type: 'button' }}
                        onClick={handleOnEditClick(id)}
                      >
                        Edit
                      </ButtonList.Item>

                      <ButtonList.Item
                        buttonProps={{ type: 'button' }}
                        onClick={() => onDelete(id)}
                        buttonClassName={styles.deleteButton}
                      >
                        Delete
                      </ButtonList.Item>
                    </ButtonList>
                  }
                />
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      {secrets.length > 0 && button}
    </div>
  );
}
